import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ChatConversationTarget,
  ChatMessage,
  ChatService,
} from './chatTypes';

interface UseChatSessionParams {
  chatService: ChatService | null;
  componentId: string;
  courseId: string;
  isAvailable: boolean;
  userId: string | null;
}

export interface ChatSession {
  clearError: () => void;
  error: string | null;
  isAvailable: boolean;
  isLoading: boolean;
  isResetting: boolean;
  messages: ChatMessage[];
  resetConversation: () => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
}

function createOptimisticMessage(text: string): ChatMessage {
  return {
    content: text,
    id: `user-pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role: 'user',
    status: 'sending',
    timestamp: Date.now(),
  };
}

function getErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Unable to reach the AI study assistant right now.';
}

export function useChatSession({
  chatService,
  componentId,
  courseId,
  isAvailable,
  userId,
}: UseChatSessionParams): ChatSession {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The conversation is identified by (course, component, user) — the backend
  // owns the provider chaining token, so there is nothing for the client to
  // thread through. That is what keeps the assembled prompt out of the UI.
  const target = useMemo<ChatConversationTarget | null>(
    () =>
      componentId && courseId && userId
        ? { componentId, courseId, userId }
        : null,
    [componentId, courseId, userId],
  );
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    if (!isAvailable || !chatService || !target) {
      setMessages([]);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);
    setError(null);

    void chatService
      .getConversation(target)
      .then((history) => {
        if (!isCancelled) {
          setMessages(history);
        }
      })
      .catch((loadError) => {
        if (!isCancelled) {
          setError(getErrorMessage(loadError));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [chatService, isAvailable, target]);

  const sendText = useCallback(
    async (text: string, optimisticMessageId?: string) => {
      const trimmed = text.trim();
      const currentTarget = targetRef.current;

      if (!trimmed || !chatService || !isAvailable || !currentTarget) {
        return;
      }

      const optimisticMessage =
        optimisticMessageId === undefined
          ? createOptimisticMessage(trimmed)
          : {
              content: trimmed,
              id: optimisticMessageId,
              role: 'user' as const,
              status: 'sending' as const,
              timestamp: Date.now(),
            };

      setError(null);
      setIsLoading(true);
      setMessages((previous) => {
        if (optimisticMessageId) {
          return previous.map((message) =>
            message.id === optimisticMessageId
              ? { ...message, error: undefined, status: 'sending' as const }
              : message,
          );
        }

        return [...previous, optimisticMessage];
      });

      try {
        const assistantMessage = await chatService.sendMessage({
          ...currentTarget,
          message: trimmed,
        });

        setMessages((previous) => [
          ...previous.map((message) =>
            message.id === optimisticMessage.id
              ? { ...message, error: undefined, status: 'sent' as const }
              : message,
          ),
          assistantMessage,
        ]);
      } catch (sendError) {
        const nextError = getErrorMessage(sendError);
        setError(nextError);
        setMessages((previous) =>
          previous.map((message) =>
            message.id === optimisticMessage.id
              ? { ...message, error: nextError, status: 'error' as const }
              : message,
          ),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [chatService, isAvailable],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      await sendText(text);
    },
    [sendText],
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const message = messages.find(
        (candidate) => candidate.id === messageId && candidate.role === 'user',
      );

      if (!message) {
        return;
      }

      await sendText(message.content, message.id);
    },
    [messages, sendText],
  );

  // Reset is a HARD DELETE server-side: the transcript is gone, not archived.
  // The confirmation lives in the panel; by the time this runs the student has
  // already confirmed.
  const resetConversation = useCallback(async () => {
    const currentTarget = targetRef.current;

    if (!chatService || !currentTarget) {
      return;
    }

    setIsResetting(true);
    setError(null);

    try {
      await chatService.resetConversation(currentTarget);
      setMessages([]);
    } catch (resetError) {
      setError(getErrorMessage(resetError));
    } finally {
      setIsResetting(false);
    }
  }, [chatService]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return useMemo(
    () => ({
      clearError,
      error,
      isAvailable,
      isLoading,
      isResetting,
      messages,
      resetConversation,
      retryMessage,
      sendMessage,
    }),
    [
      clearError,
      error,
      isAvailable,
      isLoading,
      isResetting,
      messages,
      resetConversation,
      retryMessage,
      sendMessage,
    ],
  );
}
